import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { defer, Observable, of, throwError as observableThrowError } from 'rxjs';
import { catchError, map, take, timeout } from 'rxjs/operators';
import { timeoutError } from '../../models/timeout-error';
import { TimeoutLimit } from '../../models/timeout-limit';
import { Store } from '@ngrx/store';
import { RootState } from '../../state/state';
import { loadReservation } from '../reservation-service/state/reservation-service.actions';
import { NavigationStart, Router } from '@angular/router';
import { GlobalEvent, GlobalEventService } from '../global-event-service/global-event.service';
import { ReAddSecureFlightInformationResponse } from '../../dtos/response/readd-secure-flight-information/re-add-secure-flight-information-reponse';
import { GenericResponseStatus } from '../../models/generic-response-status';
import { BulkReAddSfiResponse } from '../../dtos/response/readd-secure-flight-information/bulk-re-add-sfi-response';
import { ReAddSfiResponseStatus } from '../../dtos/response/readd-secure-flight-information/re-add-sfi-response-status';

export interface SecureFlightInfoServiceAPI {
  reAddSecureFlightInformation(confirmationCodes: string[]): Observable<BulkReAddSfiResponse>;
}

@Injectable({
  providedIn: 'root',
})
export class SecureFlightInfoService implements SecureFlightInfoServiceAPI {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly eventService = inject(GlobalEventService);

  // Extracted this method to the service so that we can manipulate this step in the mock service.
  // We need to be able to manipulate it so that the green check can display in the integration tests.
  refreshReservation(confirmationCode: string): void {
    // We want to refresh the reservation when there is a route change (such as when we change tabs).
    // This will give the user a better experience than after each save.
    this.router.events.pipe(take(1)).subscribe((e) => {
      if (e instanceof NavigationStart) {
        this.store.dispatch(loadReservation(confirmationCode));
      }
    });
  }

  /**
   * Re-adds secure flight information for multiple confirmation codes.
   */
  reAddSecureFlightInformation(confirmationCodes: string[]): Observable<BulkReAddSfiResponse> {
    const options = {
      headers: new HttpHeaders({
        background: 'false',
      }),
    };
    return this.http
      .put<ReAddSecureFlightInformationResponse>(`api/reservation/secure-flight-info/re-add/bulk`, confirmationCodes, options)
      .pipe(
        timeout({
          each: TimeoutLimit.LONG,
          with: () => defer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
        }),
        map((response) => {
          if (response.readdSfiSlices && response.readdSfiSlices.length > 0) {
            if (
              response.readdSfiSlices.every(
                (slice) => slice.addStatus === ReAddSfiResponseStatus.Success && slice.deleteStatus === ReAddSfiResponseStatus.Success
              )
            ) {
              return { response, status: GenericResponseStatus.SUCCESS };
            } else if (
              response.readdSfiSlices.every(
                (slice) => slice.addStatus !== ReAddSfiResponseStatus.Success && slice.deleteStatus !== ReAddSfiResponseStatus.Success
              )
            ) {
              return { response, status: GenericResponseStatus.FAILURE };
            }
          }
          return { response, status: GenericResponseStatus.PARTIAL_FAILURE };
        }),
        catchError((err) => {
          if (timeoutError.statusText === err.statusText) {
            this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
            return of({ response: null, status: GenericResponseStatus.TIMEOUT });
          }
          if (err?.error?.readdSfiSlices) {
            return of({ response: err?.error as ReAddSecureFlightInformationResponse, status: GenericResponseStatus.FAILURE });
          } else {
            return of({ response: null, status: GenericResponseStatus.FAILURE });
          }
        })
      );
  }
}
